import { useContext } from 'react';

import {
  cacheServiceContext,
  rpcStateGetBalance,
  rpcStateGetItem,
} from '../services';
import { casperService } from '../services/casper-service';
import { ApiGetResult } from './event-store-hooks';
import { AccountRawData } from '@block-explorer/entities';
import { NamedKeyPrefix } from '@make-software/cspr-ui';
import {
  createApiHookWithParams,
  useCallPromiseUsingDependencies,
} from './api-hooks-utils';
import { ChainGetBlockResult } from 'casper-js-sdk/dist/rpc/response';

/** UN-CACHED HOOKS */
export const useGetConnectedPeers = createApiHookWithParams(() =>
  casperService.getPeers().then((res) => {
    return res.peers;
  })
);

export const useGetBlockInfo = createApiHookWithParams(
  (params: { blockHash: string }) =>
    casperService
      .getBlockByHash(params.blockHash)
      .then((res: ChainGetBlockResult) => res)
);

export const useGetDictionaryInfo = createApiHookWithParams(
  (params: {
    dictionaryItemKey: string;
    seedUref: string;
    stateRootHash: string;
  }) =>
    casperService
      .getDictionaryItem(
        params.stateRootHash,
        params.seedUref,
        params.dictionaryItemKey
      )
      .then((res) => res)
);

/** CACHED HOOKS */

export type CacheGetResult<Entity extends any> = {
  loading: boolean;
  error: string | null;
  data: Entity | null;
};

export const useGetLastBlockInfo = (): CacheGetResult<{
  hash: string;
  timestamp: string;
  era_id: number;
  height: number;
  state_root_hash: string;
}> => {
  const { getGlobalState } = useContext(cacheServiceContext);
  const { data: statusCache, error } = getGlobalState('status');

  const loading = statusCache == null && !error;

  const data = statusCache == null ? null : statusCache.last_added_block;

  return { loading, data, error };
};

export const useGetLastProgressTime = (): CacheGetResult<{
  timestamp: number;
}> => {
  const { getGlobalState } = useContext(cacheServiceContext);
  const { data: statusCache, error } = getGlobalState('status');

  const loading = statusCache == null && !error;

  if (statusCache == null) {
    return { loading, data: null, error };
  }

  const data = {
    timestamp: config.clientside_deploy_creation_timestamp
      ? Date.now()
      : new Date(statusCache.last_progress).getTime(),
  };

  return { loading, data, error };
};

export const useGetStateRootHash = (): CacheGetResult<string> => {
  const { getGlobalState } = useContext(cacheServiceContext);
  const { data: statusCache, error } = getGlobalState('status');

  const loading = statusCache == null && !error;

  const data =
    statusCache == null || statusCache.last_added_block == null
      ? null
      : statusCache.last_added_block.state_root_hash;

  return { loading, data, error };
};

export const useGetAccountRawData = (
  params: {
    accountHash: string;
  } | null
): ApiGetResult<AccountRawData> => {
  const { data, error, loading, reloadData } = useGetStateItem<AccountRawData>(
    params?.accountHash
      ? { key: NamedKeyPrefix.ACCOUNT_HASH + params.accountHash }
      : null
  );

  return {
    data,
    error,
    loading,
    reloadData,
  };
};

export const useGetStateItem = <T extends any>(
  params: {
    key: string;
  } | null
): ApiGetResult<T | null> => {
  const {
    data: stateRootHash,
    loading: stateRootHashLoading,
    error: stateRootHashError,
  } = useGetStateRootHash();

  let promise: undefined | (() => Promise<T>);

  if (params != null && params.key && stateRootHash != null) {
    promise = () =>
      rpcStateGetItem({
        state_root_hash: stateRootHash,
        key: params.key,
      }).then((res) => {
        if (res.error) {
          // error will be handled properly by the useApiGetResultFromPromise
          throw res.error;
        }

        return res.result.stored_value;
      });
  }

  const {
    data: promiseData,
    error: promiseError,
    loading: promiseLoading,
    reloadData,
  } = useCallPromiseUsingDependencies(promise, [stateRootHash, params?.key]);

  const error = stateRootHashError || promiseError;
  const loading =
    stateRootHashLoading ||
    ((promiseData == null || promiseLoading) &&
      stateRootHashError == null &&
      promiseError == null);

  return {
    data: promiseData,
    error,
    loading,
    reloadData,
  };
};

export const useGetAccountBalance = (
  params: {
    mainPurseUref: string | null;
  } | null
): ApiGetResult<string> => {
  const {
    data: stateRootHash,
    loading: stateRootHashLoading,
    error: stateRootHashError,
  } = useGetStateRootHash();

  let promise: undefined | (() => Promise<any>);

  if (params != null && params.mainPurseUref && stateRootHash != null) {
    const mainPurseUref = params.mainPurseUref;

    promise = () =>
      rpcStateGetBalance({
        state_root_hash: stateRootHash,
        purse_uref: mainPurseUref,
      })
        .then((res) => res.result.balance_value)
        .then(
          (balance) => {
            return !balance ? '0' : balance.toString();
          },
          () => {
            throw new Error('Account Balance is not available.');
          }
        );
  }

  const {
    data: promiseData,
    error: promiseError,
    loading: promiseLoading,
    reloadData: promiseReload,
  } = useCallPromiseUsingDependencies(promise, [
    stateRootHash,
    params?.mainPurseUref,
  ]);

  const error = stateRootHashError || promiseError;
  const loading =
    stateRootHashLoading ||
    ((promiseData == null || promiseLoading) &&
      stateRootHashError == null &&
      promiseError == null);

  return {
    data: params?.mainPurseUref ? promiseData : null, // if no valid mainPurseUref return null
    error,
    loading,
    reloadData: promiseReload,
  };
};
